import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const DATE_FORMAT = 'YYYY-MM-DD';

export const usePersistentFilters = (defaultFilters) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [filters, setFilters] = useState(() => {
        const urlFilters = {};

        Object.keys(defaultFilters).forEach((key) => {
            const value = searchParams.get(key);
            if (value === null) return;

            const defaultValue = defaultFilters[key];

            if (value === '') {
                if (Array.isArray(defaultValue)) {
                    urlFilters[key] = [];
                } else if (typeof defaultValue === 'string') {
                    urlFilters[key] = '';
                } else {
                    urlFilters[key] = null;
                }
                return;
            }

            if (dayjs.isDayjs(defaultValue)) {
                urlFilters[key] = dayjs(value, DATE_FORMAT);
            } else if (typeof defaultValue === 'number') {
                urlFilters[key] = Number(value);
            } else if (Array.isArray(defaultValue)) {
                urlFilters[key] = value ? value.split(',') : [];
            } else {
                urlFilters[key] = value;
            }
        });

        return {
            ...defaultFilters,
            ...urlFilters,
        };
    });

    const applyFilters = useCallback(
        (next) => {
            const merged = { ...filters, ...next };
            setFilters(merged);

            const params = {};

            Object.entries(merged).forEach(([key, value]) => {
                const isValueEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
                const isDefaultEmpty = defaultFilters[key] === null || defaultFilters[key] === undefined || defaultFilters[key] === '' || (Array.isArray(defaultFilters[key]) && defaultFilters[key].length === 0);

                if (isValueEmpty) {
                    if (!isDefaultEmpty) {
                        params[key] = '';
                    }
                    return;
                }

                if (Array.isArray(value)) {
                    params[key] = value.join(',');
                } else if (dayjs.isDayjs(value)) {
                    params[key] = value.format(DATE_FORMAT);
                } else {
                    params[key] = String(value);
                }
            });

            setSearchParams(params, { replace: true });
            return merged;
        },
        [filters, setSearchParams, defaultFilters]
    );

    const resetFilters = useCallback(() => {
        setFilters(defaultFilters);
        setSearchParams({});
    }, [defaultFilters, setSearchParams]);

    return {
        filters,
        applyFilters,
        resetFilters,
    };
};
